import { useState, useEffect, useRef, useCallback } from "react";
import { deadlineOverride } from "../lib/utils";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import PublicBrowseShell from "../components/PublicBrowseShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Lightning, CurrencyDollar, Calendar, Bookmark, BookmarkSimple, ArrowLeft, CheckCircle, Warning, Sparkle, Users, TrendUp, Lock, Globe, Trophy, Target, Timer, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PrepKitModal } from "../components/PrepKitModal";
import SubmitReadyScore from "../components/SubmitReadyScore";
import ApplicationPacketModal from "../components/ApplicationPacketModal";

const GrantDetail = () => {
  const { grantId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [grant, setGrant] = useState(null);
  const [application, setApplication] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showPrepKit, setShowPrepKit] = useState(false);
  const [showPacket, setShowPacket] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [additionalContext, setAdditionalContext] = useState("");
  const [draftContent, setDraftContent] = useState("");
  const [unavailable, setUnavailable] = useState(null); // null | { status: 410|404, message: string }
  const [saveStatus, setSaveStatus] = useState(null); // null | "saving" | "saved" | "error"
  const [submitReady, setSubmitReady] = useState(null);
  const [scoreRefreshKey, setScoreRefreshKey] = useState(0);
  const [partnerMatches, setPartnerMatches] = useState(null);
  const autoSaveTimer = useRef(null);
  const lastSavedContent = useRef("");

  const autoSave = useCallback(async (content, appId) => {
    if (!appId || content === lastSavedContent.current) return;
    setSaveStatus("saving");
    try {
      await axios.put(`${API}/applications/${appId}`, {
        application_id: appId,
        content: content
      });
      lastSavedContent.current = content;
      setSaveStatus("saved");
    } catch {
      setSaveStatus("error");
    }
  }, []);

  const handleDraftChange = (e) => {
    const val = e.target.value;
    setDraftContent(val);
    setSaveStatus(null);
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    if (application && application.status === "draft") {
      autoSaveTimer.current = setTimeout(() => {
        autoSave(val, application.application_id);
      }, 1500);
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, []);

  const getDaysUntilDeadline = () => {
    if (!grant?.deadline) return null;
    const deadline = new Date(grant.deadline + "T23:59:59");
    const now = new Date();
    return Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
  };

  const getDeadlineUrgency = () => {
    if (deadlineOverride(grant)) return null;
    const days = getDaysUntilDeadline();
    if (days === null) return null;
    if (days < 0) return { label: "Expired", color: "text-red-500 bg-red-500/10 border-red-500/30", icon: "expired" };
    if (days <= 1) return { label: "Due tomorrow!", color: "text-red-400 bg-red-500/10 border-red-500/30", icon: "critical" };
    if (days <= 3) return { label: `${days} days left`, color: "text-orange-400 bg-orange-500/10 border-orange-500/30", icon: "warning" };
    if (days <= 7) return { label: `${days} days left`, color: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30", icon: "upcoming" };
    return { label: `${days} days left`, color: "text-zinc-400 bg-zinc-800 border-zinc-700", icon: "info" };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [grantRes, appsRes, bookmarksRes] = await Promise.all([
          axios.get(`${API}/grants/${grantId}`),
          axios.get(`${API}/applications`),
          axios.get(`${API}/bookmarks`)
        ]);

        setGrant(grantRes.data);
        
        // Check if user has existing application for this grant
        const existingApp = (appsRes.data.applications || []).find(a => a.grant_id === grantId);
        if (existingApp) {
          setApplication(existingApp);
          setDraftContent(existingApp.draft_content || "");
          lastSavedContent.current = existingApp.draft_content || "";
        }

        // Check if bookmarked
        const bookmarkedIds = (bookmarksRes.data.bookmarks || []).map(b => b.grant_id);
        setIsBookmarked(bookmarkedIds.includes(grantId));

        // Fetch partnership matches for this grant (fires and forgets — widget is optional)
        axios.get(`${API}/grants/${grantId}/partner-matches`)
          .then(r => setPartnerMatches(r.data))
          .catch(() => setPartnerMatches(null));
      } catch (error) {
        console.error("Error fetching grant:", error);
        const status = error?.response?.status;
        if (status === 410 || status === 404) {
          setUnavailable({
            status,
            message:
              error?.response?.data?.detail ||
              (status === 410
                ? "This grant is no longer available."
                : "Grant not found."),
          });
        } else {
          toast.error("Failed to load grant details");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [grantId]);

  const toggleBookmark = async () => {
    try {
      if (isBookmarked) {
        await axios.delete(`${API}/bookmarks/${grantId}`);
        setIsBookmarked(false);
        toast.success("Bookmark removed");
      } else {
        await axios.post(`${API}/bookmarks/${grantId}`, {});
        setIsBookmarked(true);
        toast.success("Grant bookmarked");
      }
    } catch (error) {
      toast.error("Failed to update bookmark");
    }
  };

  const reactivateGrant = async () => {
    if (!window.confirm("Reactivate this grant? It will become visible to all users immediately.")) return;
    try {
      await axios.post(`${API}/admin/grants/${grantId}/reactivate`);
      toast.success("Grant reactivated — now visible to users");
      // Re-fetch to update the page state
      const res = await axios.get(`${API}/grants/${grantId}`);
      setGrant(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate");
    }
  };

  const generateFlashDraft = async () => {
    if (!user?.nonprofit_profile?.org_name) {
      toast.error("Please complete your nonprofit profile first");
      navigate("/profile");
      return;
    }

    setGenerating(true);
    try {
      const res = await axios.post(`${API}/flash-draft`, {
        grant_id: grantId,
        additional_context: additionalContext
      });

      setDraftContent(res.data.draft_content);
      lastSavedContent.current = res.data.draft_content;
      setApplication({ application_id: res.data.application_id, status: "draft" });
      if (res.data.submit_ready) setSubmitReady(res.data.submit_ready);
      toast.success("Flash Draft generated!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to generate draft";
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const saveDraft = async () => {
    if (!application) return;
    setSaveStatus("saving");
    try {
      await axios.put(`${API}/applications/${application.application_id}`, {
        application_id: application.application_id,
        content: draftContent
      });
      lastSavedContent.current = draftContent;
      setSaveStatus("saved");
      setScoreRefreshKey((k) => k + 1);
      toast.success("Draft saved");
    } catch (error) {
      setSaveStatus("error");
      toast.error("Failed to save draft");
    }
  };

  const submitApplication = async () => {
    if (!application || !draftContent) {
      toast.error("Please generate or write your application first");
      return;
    }

    setSubmitting(true);
    try {
      // Save first
      await axios.put(`${API}/applications/${application.application_id}`, {
        application_id: application.application_id,
        content: draftContent
      });

      // Then submit
      const res = await axios.post(`${API}/applications/${application.application_id}/submit`, {});
      setApplication(res.data);
      toast.success("Marked as submitted in your tracker");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to mark as submitted";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const createEmptyApplication = async () => {
    try {
      const res = await axios.post(`${API}/applications`, { grant_id: grantId });
      setApplication(res.data);
      // If the backend pre-filled from profile, hydrate the editor immediately
      if (res.data.draft_content) {
        setDraftContent(res.data.draft_content);
        lastSavedContent.current = res.data.draft_content;
      }
      if (res.data.profile_prefilled) {
        toast.success("Application started — your profile info is pre-filled");
      } else {
        toast.success("Application created");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create application";
      toast.error(message);
    }
  };

  const Shell = user ? DashboardLayout : PublicBrowseShell;

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner" />
        </div>
      </Shell>
    );
  }

  if (!grant) {
    const isClosed = unavailable?.status === 410;
    return (
      <Shell>
        <div className="text-center py-16 max-w-xl mx-auto" data-testid="grant-unavailable-state">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/40 mb-5">
            <svg className="w-7 h-7 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <h2 className="text-2xl font-bold mb-3" data-testid="grant-unavailable-title">
            {isClosed ? "This grant is no longer available" : "Grant not found"}
          </h2>
          <p className="text-zinc-400 text-sm mb-6 max-w-md mx-auto leading-relaxed" data-testid="grant-unavailable-message">
            {isClosed
              ? "The funder's program is paused, suspended, or closed. We hide grants as soon as their official site shows closure language — so you never waste time on a dead application."
              : "The grant you were looking for doesn't exist or has been removed."}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button onClick={() => navigate("/grants")} className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold" data-testid="back-to-grants-btn">
              Browse {isClosed ? "currently open" : "all"} SC grants
            </Button>
            <Button onClick={() => navigate("/dashboard")} variant="outline" data-testid="back-to-dashboard-btn">
              Back to dashboard
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-6" data-testid="grant-detail-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate("/grants")}
          className="text-zinc-400 hover:text-white"
          data-testid="back-to-grants-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Grants
        </Button>

        {/* Admin-Only: Deactivated Grant Banner (only shown to admins viewing a hidden grant) */}
        {user?.role === "admin" && grant.is_expired && (
          <div
            className="bg-rose-500/15 border-2 border-rose-500/60 rounded-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3"
            data-testid="admin-deactivated-banner"
          >
            <div className="flex items-start gap-3 flex-1">
              <Warning weight="fill" className="w-6 h-6 text-rose-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-rose-300 font-bold text-sm uppercase tracking-wider" data-testid="admin-banner-title">
                  Deactivated — Visible to admins only
                </p>
                <p className="text-xs text-zinc-300 mt-1 leading-relaxed">
                  Regular users get a &ldquo;no longer available&rdquo; page when they try to reach this grant.
                  {grant.auto_deactivated_reason ? (
                    <> Auto-deactivated reason: <span className="italic text-zinc-400">&ldquo;{(grant.auto_deactivated_reason || "").slice(0, 220)}&rdquo;</span></>
                  ) : null}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:flex-shrink-0">
              <Button
                onClick={reactivateGrant}
                size="sm"
                className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
                data-testid="admin-reactivate-btn"
              >
                Reactivate
              </Button>
              <Button
                onClick={() => navigate("/admin/funder-status")}
                size="sm"
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                data-testid="admin-funder-status-link"
              >
                Funder Status →
              </Button>
            </div>
          </div>
        )}

        {/* Expired Grant Banner (shown to everyone when a grant has passed its deadline) */}
        {grant.is_expired && user?.role !== "admin" && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 flex items-center gap-3" data-testid="grant-expired-banner">
            <Warning className="w-6 h-6 text-red-400 flex-shrink-0" />
            <div>
              <p className="text-red-400 font-semibold">This grant has closed</p>
              <p className="text-sm text-zinc-400">The deadline for this grant has passed. Browse active grants to find new opportunities.</p>
            </div>
            <Button
              onClick={() => navigate("/grants")}
              variant="outline"
              className="ml-auto border-red-500/30 text-red-400 hover:bg-red-500/10 flex-shrink-0"
              data-testid="browse-active-grants-btn"
            >
              Browse Active Grants
            </Button>
          </div>
        )}

        {/* Grant Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="badge-emerald rounded-sm text-xs px-2 py-0.5 inline-block">
                {grant.category}
              </span>
              {grant.scope === "national" && (
                <span
                  className="bg-blue-500/20 text-blue-300 border border-blue-500/40 text-xs font-bold px-2 py-0.5 rounded-sm inline-flex items-center gap-1"
                  data-testid="grant-detail-national-badge"
                  title="National grant — open to SC applicants"
                >
                  <span aria-hidden>🌎</span> NATIONAL
                </span>
              )}
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">{grant.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-1 text-emerald-400 font-mono">
                <CurrencyDollar className="w-4 h-4" />
                <span>${grant.amount_min.toLocaleString()} - ${grant.amount_max.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-1 text-zinc-400">
                <Calendar className="w-4 h-4" />
                <span data-testid="grant-detail-deadline">{deadlineOverride(grant) || `Deadline: ${new Date(grant.deadline).toLocaleDateString()}`}</span>
              </div>
              {getDeadlineUrgency() && (
                <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-sm border ${getDeadlineUrgency().color}`} data-testid="deadline-urgency-badge">
                  <Timer className="w-3.5 h-3.5" />
                  {getDeadlineUrgency().label}
                </span>
              )}
              {/* Competition Tracker */}
              <div className="flex items-center gap-1 text-yellow-400">
                <Users className="w-4 h-4" />
                <span>{grant.application_count || 0} applicants</span>
              </div>
            </div>
          </div>
          
          <Button 
            variant="outline"
            onClick={toggleBookmark}
            className="border-zinc-700"
            data-testid="bookmark-grant-btn"
          >
            {isBookmarked ? (
              <>
                <Bookmark weight="fill" className="w-4 h-4 mr-2 text-emerald-400" />
                Bookmarked
              </>
            ) : (
              <>
                <BookmarkSimple className="w-4 h-4 mr-2" />
                Bookmark
              </>
            )}
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card className="bg-zinc-950 border-zinc-800">
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-zinc-300 whitespace-pre-wrap">{grant.description}</p>
              </CardContent>
            </Card>

            {/* Eligibility */}
            <Card className="bg-zinc-950 border-zinc-800">
              <CardHeader>
                <CardTitle>Eligibility</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-zinc-300">{grant.eligibility}</p>
              </CardContent>
            </Card>

            {/* Requirements */}
            {Array.isArray(grant.requirements) && grant.requirements.length > 0 && (
              <Card className="bg-zinc-950 border-zinc-800">
                <CardHeader>
                  <CardTitle>Requirements</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {grant.requirements.map((req, i) => (
                      <li key={i} className="flex items-start gap-2 text-zinc-300">
                        <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Focus Areas */}
            {Array.isArray(grant.focus_areas) && grant.focus_areas.length > 0 && (
              <Card className="bg-zinc-950 border-zinc-800">
                <CardHeader>
                  <CardTitle>Focus Areas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {grant.focus_areas.map((area, i) => (
                      <span key={i} className="badge-emerald rounded-sm px-2 py-1 text-sm">
                        {area}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Funder Insights - Premium Feature */}
            {grant.funder_name && (
              <Card className={`bg-zinc-950 border-zinc-800 ${grant.funder_insights_locked ? 'relative overflow-hidden' : ''}`}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendUp weight="duotone" className="w-5 h-5 text-emerald-400" />
                    Funder Insights
                    {grant.funder_insights_locked && (
                      <span className="badge-emerald text-xs px-2 py-0.5 ml-2">PRO</span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {grant.funder_insights_locked ? (
                    <div className="text-center py-6">
                      <Lock className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                      <p className="text-zinc-400 mb-3">Unlock detailed funder intelligence</p>
                      <Button 
                        onClick={() => navigate("/pricing")}
                        className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                      >
                        Upgrade to Premium
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Funder Info */}
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center flex-shrink-0">
                          <Globe className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                          <p className="font-semibold">{grant.funder_name}</p>
                          <p className="text-xs text-zinc-400 capitalize">{grant.funder_type} Funder</p>
                          {grant.funder_website && (
                            <a href={grant.funder_website} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-400 hover:underline">
                              Visit Website
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-3 py-3 border-y border-zinc-800">
                        <div className="text-center">
                          <p className="text-lg font-mono text-emerald-400">${(grant.avg_grant_size || 0).toLocaleString()}</p>
                          <p className="text-xs text-zinc-500">Avg Grant</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-mono text-emerald-400">{grant.acceptance_rate || 0}%</p>
                          <p className="text-xs text-zinc-500">Accept Rate</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-mono text-emerald-400">${((grant.total_awarded_annually || 0) / 1000).toFixed(0)}K</p>
                          <p className="text-xs text-zinc-500">Annual</p>
                        </div>
                      </div>

                      {/* Priorities */}
                      {Array.isArray(grant.funder_priorities) && grant.funder_priorities.length > 0 && (
                        <div>
                          <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Funder Priorities</p>
                          <div className="flex flex-wrap gap-1">
                            {grant.funder_priorities.map((p, i) => (
                              <span key={i} className="bg-zinc-800 text-zinc-300 text-xs px-2 py-1 rounded-sm">{p}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Funding History */}
                      {grant.funding_history && (
                        <div>
                          <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Funding History</p>
                          <p className="text-sm text-zinc-300">{grant.funding_history}</p>
                        </div>
                      )}

                      {/* Success Tips */}
                      {Array.isArray(grant.success_tips) && grant.success_tips.length > 0 && (
                        <div>
                          <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <Trophy className="w-3 h-3" /> Tips for Success
                          </p>
                          <ul className="space-y-1">
                            {grant.success_tips.map((tip, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                                <Target className="w-3 h-3 text-emerald-400 mt-1 flex-shrink-0" />
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Application Sidebar */}
          <div className="space-y-6">
            {/* Application Status */}
            {application && (
              <Card className="bg-zinc-950 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-base">Application Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <span className={`status-${application.status} px-3 py-1 rounded-sm text-sm font-mono uppercase`}>
                    {application.status.replace("_", " ")}
                  </span>
                </CardContent>
              </Card>
            )}

            {/* Partnership → Coalition Widget
                Two-step flow: Step 1 Form a Partnership → Step 2 Formalize a Coalition.
                Only render if there are strong partner matches AND the grant is still open. */}
            {partnerMatches && partnerMatches.total_matches > 0 && !grant.is_expired && (
              <Card className="bg-black border border-emerald-500/40 glow-border" data-testid="partnership-coalition-widget">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users weight="fill" className="w-5 h-5 text-emerald-400" />
                    <span data-testid="partner-match-count">
                      {partnerMatches.total_matches} other SC nonprofit{partnerMatches.total_matches !== 1 ? "s" : ""} match{partnerMatches.total_matches === 1 ? "es" : ""} this grant
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-xs text-zinc-400 -mt-1">
                    Applying together beats applying alone. Funders reward pooled impact.
                  </p>

                  {/* Preview list */}
                  <div className="space-y-2">
                    {partnerMatches.preview.map((m) => (
                      <div
                        key={m.user_id}
                        className="flex items-center justify-between p-2 bg-zinc-900 rounded-sm border border-zinc-800"
                        data-testid={`partner-match-${m.user_id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold truncate">{m.org_name}</p>
                          <p className="text-xs text-zinc-500 truncate">
                            {m.county ? `${m.county} County` : "SC"}
                            {m.focus_areas && m.focus_areas.length > 0 && ` · ${m.focus_areas.slice(0, 2).join(" · ")}`}
                          </p>
                        </div>
                        <span className="text-xs font-mono text-emerald-400 ml-2 flex-shrink-0">
                          {m.grant_fit_score}%
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Two-part CTA: Partnership → Coalition */}
                  <div className="space-y-3 pt-3 border-t border-zinc-800">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300 mb-1">
                        Step 1 · Form a Partnership
                      </p>
                      <p className="text-xs text-zinc-400 mb-2 leading-relaxed">
                        Connect org-to-org. Compare missions, capacity, and geography before you commit to co-applying.
                      </p>
                      <Button
                        onClick={() => navigate(`/collaborate?grant_id=${grant.grant_id}`)}
                        className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-sm"
                        data-testid="form-partnership-btn"
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Form a Partnership
                      </Button>
                    </div>

                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1">
                        Step 2 · Formalize a Coalition
                      </p>
                      <p className="text-xs text-zinc-500 mb-2 leading-relaxed">
                        Ready to co-apply? Coalitions submit as one entity with a shared outcome and pooled ask.
                      </p>
                      <Button
                        onClick={() => navigate(`/coalitions?grant_id=${grant.grant_id}`)}
                        variant="outline"
                        className="w-full border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 rounded-sm"
                        data-testid="start-coalition-btn"
                      >
                        Start a Coalition
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* How FlashGrants Helps You Apply — sets expectations before Apply Now */}
            {grant.funder_website && !grant.is_expired && (
              <Card className="bg-emerald-500/5 border border-emerald-500/30" data-testid="how-flashgrants-helps">
                <CardContent className="p-4">
                  <p className="text-xs font-bold uppercase tracking-widest text-emerald-300 mb-3 flex items-center gap-1.5">
                    <Sparkle weight="fill" className="w-3 h-3" />
                    How FlashGrants helps you apply
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2" data-testid="help-step-draft">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span className="text-zinc-300">Generate a draft application in 60 seconds with <strong className="text-emerald-300">Flash Draft</strong></span>
                    </li>
                    <li className="flex items-start gap-2" data-testid="help-step-prep">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span className="text-zinc-300">Auto-compile your required docs with <strong className="text-emerald-300">One-Click Apply Prep</strong></span>
                    </li>
                    <li className="flex items-start gap-2" data-testid="help-step-reminder">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span className="text-zinc-300">Get a deadline reminder 48 hours before the cutoff</span>
                    </li>
                    <li className="flex items-start gap-2 pt-1 border-t border-emerald-500/20 mt-2" data-testid="help-step-submit">
                      <span className="text-emerald-400 mt-0.5 flex-shrink-0 font-bold">→</span>
                      <span className="text-zinc-400 text-xs italic">Then submit on the funder&apos;s own portal — we get you 80% there in 20% of the time.</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Apply on Funder's Site */}
            {grant.funder_website && !grant.is_expired && (
              <Button
                onClick={() => setShowPrepKit(true)}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-sm py-3"
                data-testid="apply-funder-site-btn"
              >
                <Lightning weight="fill" className="w-5 h-5 mr-2" />
                Apply Now
              </Button>
            )}

            {/* One-Click Apply Prep — saves ~50 min per application */}
            {!grant.is_expired && (
              <Button
                onClick={() => setShowPacket(true)}
                variant="outline"
                className="w-full border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-300 rounded-sm py-3"
                data-testid="prep-application-btn"
              >
                <Sparkle weight="fill" className="w-4 h-4 mr-2" />
                Prep My Application
              </Button>
            )}

            {/* Application Packet — copyable answers + document checklist */}
            <ApplicationPacketModal
              grantId={grant.grant_id}
              isOpen={showPacket}
              onClose={() => setShowPacket(false)}
            />

            {/* Prep Kit Modal */}
            {grant && (
              <PrepKitModal
                grant={grant}
                open={showPrepKit}
                onOpenChange={setShowPrepKit}
              />
            )}

            {/* Flash Draft Generator */}
            {grant.is_expired ? (
              <Card className="bg-zinc-950 border-zinc-800 opacity-60">
                <CardContent className="p-6 text-center">
                  <Warning className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                  <p className="text-sm text-zinc-500">Applications are closed for this grant</p>
                </CardContent>
              </Card>
            ) : (
            <Card className="bg-black border border-emerald-500/40 glow-border">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Lightning weight="fill" className="w-5 h-5 text-emerald-400" />
                  Flash Draft
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!user?.nonprofit_profile?.org_name ? (
                  <div className="text-center py-4">
                    <Warning className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                    <p className="text-sm text-zinc-400 mb-3">Complete your profile to use Flash Draft</p>
                    <Button 
                      onClick={() => navigate("/profile")}
                      className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                      data-testid="complete-profile-btn"
                    >
                      Complete Profile
                    </Button>
                  </div>
                ) : application?.status === "submitted" ? (
                  <p className="text-sm text-zinc-400 text-center py-4">
                    Already marked as submitted in your tracker
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-zinc-400">
                      Let us generate a compelling grant application based on your nonprofit profile and this grant&apos;s requirements.
                    </p>

                    {/* Pre-flight check: warn if mission is short — strongly correlates with weak Flash Draft output */}
                    {(() => {
                      const missionText = (user?.nonprofit_profile?.mission || "").trim();
                      const wordCount = missionText ? missionText.split(/\s+/).length : 0;
                      // Threshold: <30 words OR completely empty triggers the warning
                      const needsWork = !missionText || wordCount < 30;
                      const dismissed = sessionStorage.getItem(`fg_preflight_dismissed_${grantId}`) === "1";
                      if (!needsWork || dismissed) return null;
                      return (
                        <div
                          className="rounded-sm border-2 border-amber-500/40 bg-amber-500/8 p-4 space-y-3"
                          data-testid="flash-draft-preflight-warning"
                        >
                          <div className="flex items-start gap-3">
                            <div className="shrink-0 w-8 h-8 rounded-sm bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                              <Sparkle weight="duotone" className="w-4 h-4 text-amber-300" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-bold text-amber-200 mb-1">
                                Your mission statement is {wordCount === 0 ? "missing" : `only ${wordCount} words`}.
                              </p>
                              <p className="text-[12px] text-zinc-400 leading-snug">
                                Flash Drafts pull directly from your mission. Running the <b>Mission Diagnostic</b> first sharpens the language funders look for &mdash; users who run it score ~40% higher on funder alignment.
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <Button
                              size="sm"
                              onClick={() => navigate("/formation/mission-diagnostic")}
                              className="bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs"
                              data-testid="preflight-run-diagnostic-btn"
                            >
                              Run Mission Diagnostic first (recommended)
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                sessionStorage.setItem(`fg_preflight_dismissed_${grantId}`, "1");
                                // Force re-render by toggling a state. Use a no-op setState pattern via setAdditionalContext.
                                setAdditionalContext((v) => v);
                                toast("Skipping diagnostic — generating with current mission");
                              }}
                              className="border-zinc-700 text-zinc-300 hover:bg-zinc-900 text-xs"
                              data-testid="preflight-skip-btn"
                            >
                              Skip &amp; generate anyway
                            </Button>
                          </div>
                        </div>
                      );
                    })()}

                    <Textarea 
                      placeholder="Add any additional context or specific points you'd like to include..."
                      value={additionalContext}
                      onChange={(e) => setAdditionalContext(e.target.value)}
                      className="bg-zinc-900 border-zinc-800 min-h-20"
                      data-testid="additional-context-input"
                    />
                    <Button 
                      onClick={generateFlashDraft}
                      disabled={generating}
                      className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm cta-hover"
                      data-testid="generate-flash-draft-btn"
                    >
                      {generating ? (
                        <>
                          <div className="loading-spinner mr-2" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkle weight="fill" className="w-4 h-4 mr-2" />
                          Generate Flash Draft
                        </>
                      )}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
            )}

            {/* Manual Application */}
            {!grant.is_expired && !application && user?.nonprofit_profile?.org_name && (
              <Button 
                variant="outline"
                onClick={createEmptyApplication}
                className="w-full border-zinc-700"
                data-testid="manual-application-btn"
              >
                Write Application Manually
              </Button>
            )}
          </div>
        </div>

        {/* Draft Editor */}
        {application && application.status !== "submitted" && (
          <>
            {draftContent && (
              <SubmitReadyScore
                applicationId={application.application_id}
                draftContent={draftContent}
                initialScore={submitReady}
                refreshKey={scoreRefreshKey}
              />
            )}
          <Card className="bg-zinc-950 border-zinc-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle>Your Application</CardTitle>
                  {saveStatus === "saving" && (
                    <span className="flex items-center gap-1 text-xs text-zinc-500" data-testid="auto-save-saving">
                      <div className="w-2 h-2 rounded-full bg-zinc-500 animate-pulse" />
                      Saving...
                    </span>
                  )}
                  {saveStatus === "saved" && (
                    <span className="flex items-center gap-1 text-xs text-emerald-400" data-testid="auto-save-saved">
                      <CheckCircle className="w-3.5 h-3.5" />
                      Auto-saved
                    </span>
                  )}
                  {saveStatus === "error" && (
                    <span className="flex items-center gap-1 text-xs text-red-400" data-testid="auto-save-error">
                      <Warning className="w-3.5 h-3.5" />
                      Save failed
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={saveDraft}
                    className="border-zinc-700"
                    data-testid="save-draft-btn"
                  >
                    <FloppyDisk className="w-4 h-4 mr-1" />
                    Save
                  </Button>
                  <Button 
                    onClick={submitApplication}
                    disabled={submitting || !draftContent}
                    className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                    data-testid="submit-application-btn"
                    title="Marks this application as submitted in your FlashGrants tracker. You'll still need to submit to the funder via their own portal."
                  >
                    {submitting ? "Marking…" : "Mark as Submitted"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {application?.profile_prefilled && (
                <div className="mb-4 flex items-start gap-3 p-3 rounded-md bg-emerald-950/30 border border-emerald-900" data-testid="profile-prefilled-banner">
                  <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-emerald-200 leading-relaxed">
                    <span className="font-semibold">Auto-filled from your nonprofit profile.</span>{" "}
                    <span className="text-emerald-300/80">Edit any field below — your profile data was used as a starting point.</span>
                  </p>
                </div>
              )}
              <Textarea 
                value={draftContent}
                onChange={handleDraftChange}
                placeholder="Write your grant application here..."
                className="bg-zinc-900 border-zinc-800 min-h-96 font-mono text-sm"
                data-testid="application-content-input"
              />
              <p className="text-xs text-zinc-600 mt-2">Your draft is auto-saved as you type</p>
              <div className="mt-3 flex items-start gap-2 p-3 rounded-md bg-zinc-900/60 border border-zinc-800" data-testid="submit-explainer">
                <Warning className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-zinc-400 leading-relaxed">
                  <span className="font-semibold text-zinc-300">&ldquo;Mark as Submitted&rdquo; is a tracker only.</span>{" "}
                  FlashGrants does not submit to the funder for you. Copy your finalized draft into the funder&apos;s own application portal, then click this button to log it in your tracker.
                </p>
              </div>
            </CardContent>
          </Card>
          </>
        )}

        {/* Submitted Application View */}
        {application && application.status !== "draft" && draftContent && (
          <Card className="bg-zinc-950 border-zinc-800">
            <CardHeader>
              <CardTitle>Tracked as Submitted</CardTitle>
              <p className="text-xs text-zinc-500 mt-1">Logged in your FlashGrants tracker. Make sure you&apos;ve also submitted to the funder via their portal.</p>
            </CardHeader>
            <CardContent>
              <div className="prose prose-invert prose-sm max-w-none">
                <pre className="whitespace-pre-wrap text-zinc-300 font-mono text-sm bg-zinc-900 p-4 rounded-sm">
                  {draftContent}
                </pre>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Shell>
  );
};

export default GrantDetail;
